import { getToken } from "./auth"
import axios from "axios";
import type { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import {useSettingsStore} from '@/api/store/settings';
import {Capacitor} from '@capacitor/core';

declare module 'axios' {
  interface InternalAxiosRequestConfig {
    skipGlobalLoading?: boolean;
  }
}

export function isAxiosError(value: any): value is AxiosError {
  return typeof value?.response === "object";
}

export abstract class HttpService {
  protected readonly http: AxiosInstance;
  protected route: string;

  protected constructor(protected readonly path: string) {
    // Add fallback for mobile builds where env vars might not be available
    const baseURL = process.env.VUE_APP_API_URL || 'https://api.wellsum.io/api/v3';
    
    // Debug logging to help identify the issue
    console.log('HTTP Service Base URL:', baseURL);
    console.log('Environment VUE_APP_API_URL:', process.env.VUE_APP_API_URL);
    console.log('Platform:', Capacitor.getPlatform());
    
    this.route = baseURL + path;
    console.log('Full route for', path, ':', this.route);

    this.http = axios.create();

    this.http.interceptors.request.use((request: InternalAxiosRequestConfig) => {
      if (!request.skipGlobalLoading) {
        const settingsStore = useSettingsStore()
        settingsStore.startLoading()
      }

      const token = getToken();
      if (request.headers && token) {
        request.headers.Authorization = `Bearer ${token}`;
      }

      // Add explicit User-Agent for iOS to prevent server-side filtering
      if (Capacitor.getPlatform() === 'ios') {
        request.headers['User-Agent'] = 'Swandoola-Clients-iOS/1.0 (iPhone; iOS) Capacitor/6.0.0';
      } else if (Capacitor.getPlatform() === 'android') {
        request.headers['User-Agent'] = 'Swandoola-Clients-Android/1.0 (Android) Capacitor/6.0.0';
      }

      return request;
    });

    this.http.interceptors.response.use(
      (response) => {
        if (!response.config.skipGlobalLoading) {
          const settingsStore = useSettingsStore()
          settingsStore.stopLoading()
        }
        return response;
      },
      (error) => {
        if (!error.config?.skipGlobalLoading) {
          const settingsStore = useSettingsStore()
          settingsStore.stopLoading()
        }

        return Promise.reject(error);
      }
    );
  }

  protected createParams(record: Record<string, any>): URLSearchParams {
    const params: URLSearchParams = new URLSearchParams();

    for (const key in record) {
      if (Object.prototype.hasOwnProperty.call(record, key)) {
        const value: any = record[key];

        if (value !== null && value !== undefined) {
          params.append(key, value);
        } else {
          console.debug(
            `Param key '${key}' was null or undefined and will be ignored`
          );
        }
      }
    }

    return params;
  }

  protected handleResponse<T>(response: AxiosResponse<T>): T {

    return response.data;
  }

  protected handleError(error: unknown): never {
    console.error('HTTP Service Error:', error);

    if (isAxiosError(error)) {
      if (error.response?.status === 401) {
        console.log('Unauthorized, redirecting to login');
        window.location.href = '/#/login';
      }
      // Pass the axios error through so we can handle it properly
      throw error;
    }

    // For non-axios errors, wrap them properly
    if (error instanceof Error) {
      throw error;
    }
    
    throw new Error('An unexpected error occurred');
  }
}